import { getAuth } from "firebase/auth";

import { getAPIURLv2, xhttp } from "../Fetchv2.js";
import { PGSchema } from "../schemas/pg.schema.js";

export type GraduandosFieldKey =
	| "BARCODE"
	| "COD_EVALUACION"
	| "NUM_BOLETA"
	| "CODIGO_E"
	| "COD_DEPARTAMENTO_E"
	| "NOM_DEPARTAMENTO_E"
	| "COD_MUNICIPIO_E"
	| "MUNICIPIO_ESTABLECIMIENTO"
	| "COD_NIVEL"
	| "NOM_NIVEL"
	| "COD_SECTOR"
	| "NOM_SECTOR"
	| "COD_JORNADA"
	| "NOM_JORNADA"
	| "COD_PLAN"
	| "NOM_PLAN"
	| "NOM_AREA"
	| "NOM_CICLO"
	| "DEPARTAMENTAL"
	| "COD_ALUMNO"
	| "CODIGO_PERSONAL"
	| "NOMBRE1"
	| "NOMBRE2"
	| "APELLIDO1"
	| "APELLIDO2"
	| "ANIO_CIERRE"
	| "FECHA_NACIMIENTO"
	| "NEE"
	| "MODALIDAD_CARGA"
	| "MOMENTO"
	| "EVALUADOS"
	| "TIPO_EVALUACION"
	| "REGISTRO"
	| "ID_FACTORES"
	| "ID_LECTURA"
	| "ID_MATEMATICA"
	| "ID_ENCUESTA"
	| "FECHA_SOLICITUD"
	| "CONFIG"
	| "OBSERVACIONES"
	| "_skip";

export type GraduandosScheduleConfig = {
	startDate: string;
	endDate: string;
	endTime: string;
	slots: { factores: string; lectura: string; matematica: string; encuesta: string };
};

export type GraduandosImportOptions = {
	upsertUsers: boolean;
	assignTests: boolean;
	certName: string;
	schedule: GraduandosScheduleConfig;
	fechaSolicitudFilter?: string;
};

export type GraduandosImportBody = {
	rows: Record<string, string | number>[];
	columnMapping: Record<string, GraduandosFieldKey>;
	options: GraduandosImportOptions;
};

export type GraduandosValidationError = {
	row: number;
	field?: GraduandosFieldKey | string;
	column?: string;
	message: string;
};

export type GraduandosImportEvent =
	| { type: "phase"; phase: "validating" | "upserting" | "assigning" | "done" }
	| { type: "progress"; current: number; total: number; message: string }
	| { type: "row_error"; row: number; field?: string; column?: string; message: string }
	| {
			type: "summary";
			processed: number;
			upserted: number;
			assigned: number;
			errors: GraduandosValidationError[];
	  }
	| { type: "error"; message: string };

export type GraduandosValidateResult = {
	total: number;
	valid: number;
	errors: GraduandosValidationError[];
};

// @ts-ignore
export const GRADUANDOS_FIELD_LABELS: Record<GraduandosFieldKey, string> = {
	BARCODE: "Código de barras",
	COD_EVALUACION: "Código evaluación",
	NUM_BOLETA: "Número boleta",
	CODIGO_E: "Código establecimiento",
	COD_DEPARTAMENTO_E: "Código departamento",
	NOM_DEPARTAMENTO_E: "Departamento",
	COD_MUNICIPIO_E: "Código municipio",
	MUNICIPIO_ESTABLECIMIENTO: "Municipio establecimiento",
	COD_NIVEL: "Código nivel",
	NOM_NIVEL: "Nivel",
	COD_SECTOR: "Código sector",
	NOM_SECTOR: "Sector",
	COD_JORNADA: "Código jornada",
	NOM_JORNADA: "Jornada",
	COD_PLAN: "Código plan",
	NOM_PLAN: "Plan",
	NOM_AREA: "Área",
	NOM_CICLO: "Ciclo",
	DEPARTAMENTAL: "Departamental",
	COD_ALUMNO: "Código alumno",
	CODIGO_PERSONAL: "Código personal / DPI",
	NOMBRE1: "Primer nombre",
	NOMBRE2: "Segundo nombre",
	APELLIDO1: "Primer apellido",
	APELLIDO2: "Segundo apellido",
	ANIO_CIERRE: "Año cierre",
	FECHA_NACIMIENTO: "Fecha nacimiento",
	NEE: "NEE",
	MODALIDAD_CARGA: "Modalidad carga",
	MOMENTO: "Momento",
	EVALUADOS: "Evaluados",
	TIPO_EVALUACION: "Tipo evaluación",
	REGISTRO: "Registro",
	ID_FACTORES: "ID Factores",
	ID_LECTURA: "ID Lectura",
	ID_MATEMATICA: "ID Matemática",
	ID_ENCUESTA: "ID Encuesta",
	FECHA_SOLICITUD: "Fecha solicitud",
	CONFIG: "Config",
	OBSERVACIONES: "Observaciones"
};

export function normalizeGraduandosHeader(h: string): string {
	return h
		.trim()
		.toLowerCase()
		.replace(/\*/g, "")
		.replace(/\s+/g, "_")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");
}

const headerToField = new Map<string, GraduandosFieldKey>();
for (const [key, label] of Object.entries(GRADUANDOS_FIELD_LABELS)) {
	headerToField.set(normalizeGraduandosHeader(key), key as GraduandosFieldKey);
	headerToField.set(normalizeGraduandosHeader(label), key as GraduandosFieldKey);
}
headerToField.set("dpi", "CODIGO_PERSONAL");
headerToField.set("codigo_personal", "CODIGO_PERSONAL");
headerToField.set("cod_est", "CODIGO_E");

export function formatGraduandosError(err: GraduandosValidationError, fileColumnByField?: Map<string, string>): string {
	const col = err.column ?? (err.field && fileColumnByField?.get(String(err.field))) ?? (err.field ? GRADUANDOS_FIELD_LABELS[err.field as keyof typeof GRADUANDOS_FIELD_LABELS] : undefined) ?? "—";
	const fila = err.row > 0 ? `Fila ${err.row}` : "General";
	return `${fila} · Columna: ${col} · ${err.message}`;
}

export function autoMapGraduandosColumns(fileHeaders: string[]): Record<string, GraduandosFieldKey> {
	const mapping: Record<string, GraduandosFieldKey> = {};
	for (const h of fileHeaders) {
		mapping[h] = headerToField.get(normalizeGraduandosHeader(h)) ?? "_skip";
	}
	return mapping;
}

export class DigeducaGraduandosImportApi {
	validate(pgid: PGSchema.pages["id"], token: string, body: GraduandosImportBody) {
		return xhttp<GraduandosValidateResult>(`/pages/${pgid}/digeduca/graduandos/validate`, "POST", body, {
			headers: { "app-token": token }
		});
	}

	async streamImport(pgid: PGSchema.pages["id"], token: string, body: GraduandosImportBody, onEvent: (event: GraduandosImportEvent) => void, signal?: AbortSignal): Promise<void> {
		const idToken = (await getAuth().currentUser?.getIdToken()) || "no-auth";
		const url = new URL(getAPIURLv2() + `/pages/${pgid}/digeduca/graduandos/import`);

		const response = await fetch(url.href, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `bearer ${idToken}`,
				"app-token": token,
				"Cache-Control": "no-cache"
			},
			body: JSON.stringify(body),
			signal
		});

		if (!response.ok || !response.body) {
			onEvent({ type: "error", message: `HTTP ${response.status}` });
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });

			const lines = buffer.split("\n\n");
			buffer = lines.pop() ?? "";

			for (const line of lines) {
				if (!line.startsWith("data: ")) continue;
				try {
					const event = JSON.parse(line.slice(6)) as GraduandosImportEvent;
					onEvent(event);
				} catch {
					// skip malformed chunk
				}
			}
		}

		if (buffer.startsWith("data: ")) {
			try {
				const event = JSON.parse(buffer.slice(6)) as GraduandosImportEvent;
				onEvent(event);
			} catch {}
		}
	}
}
